/**
 * Results List Page
 * View all assessment attempts/results
 */

import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { attemptsApi, assessmentsApi } from '../api';
import { Assessment } from '../../types/models';
import { ConfirmDeleteModal } from '../components/ConfirmDeleteModal';
import { AlertModal } from '../components/AlertModal';

interface DeleteTarget {
  id: string
  assessmentTitle: string
}

export const ResultsList: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [attempts, setAttempts] = useState<any[]>([]);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 });
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [alert, setAlert] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  
  const [selectedAssessment, setSelectedAssessment] = useState<string>(searchParams.get('assessment_id') || '');
  const [selectedStatus, setSelectedStatus] = useState<string>(searchParams.get('status') || '');
  const [userIdInput, setUserIdInput] = useState<string>(searchParams.get('user_id') || '');
  const [selectedUserId, setSelectedUserId] = useState<string>(searchParams.get('user_id') || '');
  const [userIdError, setUserIdError] = useState<string | null>(null);

  const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  useEffect(() => {
    loadAssessments();
  }, []);

  useEffect(() => {
    loadAttempts();
  }, [selectedAssessment, selectedStatus, selectedUserId, pagination.page]);

  const loadAssessments = async () => {
    try {
      const result = await assessmentsApi.list({ limit: 100 });
      setAssessments(result.data);
    } catch (err: any) {
      console.error('Failed to load assessments:', err);
    }
  };

  const loadAttempts = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: pagination.page,
        limit: pagination.limit,
      };
      
      if (selectedAssessment) {
        params.assessment_id = selectedAssessment;
      }
      
      if (selectedStatus) {
        params.status = selectedStatus;
      }

      if (selectedUserId) {
        params.user_id = selectedUserId;
      }

      const result = await attemptsApi.list(params);
      setAttempts(result.data);
      setPagination(result.pagination);
    } catch (err: any) {
      setError(err.message || 'Failed to load results');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field: 'assessment' | 'status', value: string) => {
    if (field === 'assessment') {
      setSelectedAssessment(value);
      setSearchParams({ ...Object.fromEntries(searchParams), assessment_id: value || '' });
    } else {
      setSelectedStatus(value);
      setSearchParams({ ...Object.fromEntries(searchParams), status: value || '' });
    }
    setPagination({ ...pagination, page: 1 });
  };

  const applyUserIdFilter = () => {
    const trimmed = userIdInput.trim();
    if (trimmed && !UUID_REGEX.test(trimmed)) {
      setUserIdError('Enter a valid UUID (e.g., 550e8400-e29b-41d4-a716-446655440000)');
      return;
    }
    setUserIdError(null);
    setSelectedUserId(trimmed);
    const next = { ...Object.fromEntries(searchParams) };
    if (trimmed) {
      next.user_id = trimmed;
    } else {
      delete next.user_id;
    }
    setSearchParams(next);
    setPagination({ ...pagination, page: 1 });
  };

  const clearUserIdFilter = () => {
    setUserIdInput('');
    setUserIdError(null);
    setSelectedUserId('');
    const next = { ...Object.fromEntries(searchParams) };
    delete next.user_id;
    setSearchParams(next);
    setPagination({ ...pagination, page: 1 });
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleString();
  };

  const formatTime = (seconds: number | null) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const formatStatus = (status: string) => {
    switch (status) {
      case 'in_progress':
        return 'In Progress';
      case 'completed':
        return 'Completed';
      case 'abandoned':
        return 'Abandoned';
      default:
        return status;
    }
  };

  const handleDeleteClick = (attempt: any) => {
    setDeleteTarget({
      id: attempt.id,
      assessmentTitle: attempt.assessment?.title || 'this assessment',
    });
  };

  const handleDelete = async () => {
    if (!deleteTarget) {
      return;
    }

    try {
      setDeleting(true);
      await attemptsApi.delete(deleteTarget.id);
      setDeleteTarget(null);
      setAlert({ message: 'Result deleted successfully', type: 'success' });
      if (pagination.page === 1) {
        await loadAttempts();
      } else {
        setPagination((prev) => ({ ...prev, page: 1 }));
      }
    } catch (err: any) {
      setDeleteTarget(null);
      setAlert({
        message: err.message || 'Failed to delete result',
        type: 'error',
      });
    } finally {
      setDeleting(false);
    }
  };

  if (loading && attempts.length === 0) {
    return <div className="loading">Loading results...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  return (
    <div className="results-list">
      <div className="results-header">
        <h1>Assessment Results</h1>
      </div>

      <div className="results-filters">
        <div className="filter-group">
          <label>
            <span>Filter by Assessment:</span>
            <select
              value={selectedAssessment}
              onChange={(e) => handleFilterChange('assessment', e.target.value)}
            >
              <option value="">All Assessments</option>
              {assessments.map((assessment) => (
                <option key={assessment.id} value={assessment.id}>
                  {assessment.title}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="filter-group">
          <label>
            <span>Filter by Status:</span>
            <select
              value={selectedStatus}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="completed">Completed</option>
              <option value="in_progress">In Progress</option>
              <option value="abandoned">Abandoned</option>
            </select>
          </label>
        </div>

        <div className="filter-group filter-group-user">
          <label htmlFor="user-id-filter">Filter by User ID:</label>
          <div className="filter-user-row">
            <input
              id="user-id-filter"
              type="text"
              value={userIdInput}
              onChange={(e) => setUserIdInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') applyUserIdFilter();
              }}
              placeholder="User UUID"
              autoComplete="off"
              spellCheck={false}
              data-1p-ignore
              data-lpignore="true"
              data-form-type="other"
            />
            <button
              type="button"
              className="btn btn-primary"
              onClick={applyUserIdFilter}
            >
              Apply
            </button>
            {selectedUserId && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={clearUserIdFilter}
              >
                Clear
              </button>
            )}
          </div>
          {userIdError && <div className="error filter-error">{userIdError}</div>}
        </div>
      </div>

      <div className="results-table">
        <table>
          <thead>
            <tr>
              <th>Assessment</th>
              <th>User ID</th>
              <th>Status</th>
              <th>Score</th>
              <th>Time Spent</th>
              <th>Started</th>
              <th>Completed</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {attempts.length === 0 ? (
              <tr>
                <td colSpan={8}>No results found.</td>
              </tr>
            ) : (
              attempts.map((attempt) => (
                <tr key={attempt.id}>
                  <td>
                    {attempt.assessment ? (
                      <Link to={`/assessments/${attempt.assessment.id}`}>
                        {attempt.assessment.title}
                      </Link>
                    ) : (
                      'N/A'
                    )}
                  </td>
                  <td>{attempt.user_id}</td>
                  <td>
                    <span className={`status-badge status-${attempt.status}`}>
                      {formatStatus(attempt.status)}
                    </span>
                  </td>
                  <td>
                    {attempt.score_percent !== null ? (
                      <>
                        {attempt.score_raw !== null && attempt.score_max !== null
                          ? `${attempt.score_raw}/${attempt.score_max}`
                          : ''}{' '}
                        ({attempt.score_percent.toFixed(1)}%)
                        {attempt.passed !== null && (
                          <span className={attempt.passed ? 'passed' : 'failed'}>
                            {attempt.passed ? ' ✓ Passed' : ' ✗ Failed'}
                          </span>
                        )}
                      </>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>{formatTime(attempt.time_spent_seconds)}</td>
                  <td>{formatDate(attempt.started_at)}</td>
                  <td>
                    {attempt.completed_at ? formatDate(attempt.completed_at) : '-'}
                  </td>
                  <td className="actions">
                    <Link
                      to={`/results/${attempt.id}`}
                      className="btn btn-small btn-primary"
                    >
                      View Details
                    </Link>
                    <button
                      type="button"
                      onClick={() => handleDeleteClick(attempt)}
                      className="btn btn-small btn-danger"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination.total_pages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
            disabled={pagination.page === 1}
            className="btn btn-secondary"
          >
            Previous
          </button>
          <span>
            Page {pagination.page} of {pagination.total_pages}
          </span>
          <button
            onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
            disabled={pagination.page >= pagination.total_pages}
            className="btn btn-secondary"
          >
            Next
          </button>
        </div>
      )}

      <ConfirmDeleteModal
        isOpen={deleteTarget !== null}
        onClose={() => {
          if (!deleting) {
            setDeleteTarget(null);
          }
        }}
        onConfirm={handleDelete}
        title="Delete Assessment Attempt"
        message='Are you sure you want to delete this assessment attempt? This will REMOVE this attempt for this user!'
        loading={deleting}
      />

      <AlertModal
        isOpen={alert !== null}
        onClose={() => setAlert(null)}
        message={alert?.message || ''}
        type={alert?.type || 'info'}
      />
    </div>
  );
};
